'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Lock, ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
type CellVal = boolean | string | null | 'locked' | 'enterprise_no';

interface ComparisonRow {
  feature: string;
  starter: CellVal;
  growth: CellVal;
  scale: CellVal;
  enterprise: CellVal;
}

interface ComparisonSection {
  section: string;
  rows: ComparisonRow[];
}

// ─── Plan Data ────────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 79,
    badge: null,
    badgeBg: null,
    cta: 'Start Free Trial',
    ctaStyle: 'ghost' as const,
    href: '/start',
    popular: false,
    subline: '7-day free trial · No credit card required',
  },
  {
    id: 'growth',
    name: 'Growth',
    monthlyPrice: 149,
    badge: 'Most Popular',
    badgeBg: '#F97316',
    cta: 'Start Free Trial',
    ctaStyle: 'orange' as const,
    href: '/start',
    popular: true,
    subline: '7-day free trial · No credit card required',
  },
  {
    id: 'scale',
    name: 'Scale',
    monthlyPrice: 299,
    badge: 'Best for Agencies',
    badgeBg: '#3B82F6',
    cta: 'Start Free Trial',
    ctaStyle: 'ghost' as const,
    href: '/start',
    popular: false,
    subline: '7-day free trial · No credit card required',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: null,
    badge: null,
    badgeBg: null,
    cta: 'Contact Sales',
    ctaStyle: 'dark' as const,
    href: '/contact',
    popular: false,
    subline: 'Custom pricing · Talk to our team',
  },
];

// ─── Comparison Table Data ────────────────────────────────────────────────────
const COMPARISON: ComparisonSection[] = [
  {
    section: 'LIMITS',
    rows: [
      { feature: 'Monthly ad spend', starter: 'Up to $100,000/mo', growth: 'Up to $250,000/mo', scale: 'Up to $1M/mo', enterprise: 'Custom' },
      { feature: 'User seats', starter: '1', growth: '3', scale: '8', enterprise: 'Unlimited' },
      { feature: 'Ad accounts', starter: '5', growth: '10', scale: '25', enterprise: 'Unlimited' },
      { feature: 'Data history', starter: '30 days', growth: '90 days', scale: 'Unlimited', enterprise: 'Unlimited' },
    ],
  },
  {
    section: 'CORE FEATURES',
    rows: [
      { feature: 'Weekly Cut/Keep/Scale', starter: true, growth: true, scale: true, enterprise: true },
      { feature: 'Budget Reallocation', starter: true, growth: true, scale: true, enterprise: true },
      { feature: 'All Platform Connections', starter: 'Up to 2', growth: 'Up to 3', scale: 'Up to 5', enterprise: 'All Platforms' },
      { feature: 'Real-Time Alerts (email)', starter: true, growth: true, scale: true, enterprise: true },
      { feature: 'Real-Time Alerts (in-app)', starter: 'locked', growth: true, scale: true, enterprise: true },
    ],
  },
  {
    section: 'ADVANCED FEATURES',
    rows: [
      { feature: 'Creative Fatigue Detection', starter: 'locked', growth: '100 scans /mo', scale: '300 scans /mo', enterprise: 'Unlimited' },
      { feature: 'AI Chat Assistant', starter: 'locked', growth: true, scale: true, enterprise: true },
      { feature: 'What-If Scenarios', starter: 'locked', growth: 'locked', scale: true, enterprise: true },
      { feature: 'Historical Trend Analysis', starter: 'locked', growth: 'locked', scale: true, enterprise: true },
    ],
  },
  {
    section: 'WEBINARS',
    rows: [
      { feature: 'Webinar Hosting', starter: 'locked', growth: '1 live + 2 recorded /mo', scale: '3 live + 10 recorded /mo', enterprise: 'Unlimited + Featured' },
    ],
  },
  {
    section: 'COLLABORATION',
    rows: [
      { feature: 'Team Collaboration + Assignments', starter: 'locked', growth: 'Up to 3 seats', scale: 'Up to 8 seats', enterprise: 'Unlimited' },
      { feature: 'Agency Client Management', starter: 'locked', growth: 'locked', scale: 'Up to 10 clients', enterprise: 'Unlimited' },
    ],
  },
  {
    section: 'SUPPORT',
    rows: [
      { feature: 'Email Support', starter: true, growth: true, scale: true, enterprise: true },
      { feature: 'Priority Support', starter: 'locked', growth: 'locked', scale: true, enterprise: true },
    ],
  },
  {
    section: 'ENTERPRISE ONLY',
    rows: [
      { feature: 'All features in Scale', starter: 'enterprise_no', growth: 'enterprise_no', scale: 'enterprise_no', enterprise: true },
      { feature: 'Multi-account agency platform', starter: 'enterprise_no', growth: 'enterprise_no', scale: 'enterprise_no', enterprise: true },
      { feature: 'Custom integrations', starter: 'enterprise_no', growth: 'enterprise_no', scale: 'enterprise_no', enterprise: true },
      { feature: 'Custom onboarding', starter: 'enterprise_no', growth: 'enterprise_no', scale: 'enterprise_no', enterprise: true },
    ],
  },
];

// ─── Cell Renderer ────────────────────────────────────────────────────────────
function CellValue({ value, isGrowth }: { value: CellVal; isGrowth?: boolean }) {
  if (value === true) {
    return (
      <span style={{ display: 'flex', justifyContent: 'center' }}>
        <span style={{ color: '#22C55E', fontSize: '16px', fontWeight: 700, lineHeight: 1 }}>✓</span>
      </span>
    );
  }
  if (value === 'enterprise_no') {
    return (
      <span style={{ display: 'flex', justifyContent: 'center' }}>
        <span style={{ color: '#CBD5E1', fontSize: '16px', fontWeight: 700, lineHeight: 1 }}>✗</span>
      </span>
    );
  }
  if (value === 'locked') {
    return (
      <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
        <Lock size={13} style={{ color: '#334155', flexShrink: 0 }} />
        <span style={{ color: '#334155', fontSize: '13px' }}>—</span>
      </span>
    );
  }
  if (value === null || value === false) {
    return (
      <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
        <Lock size={13} style={{ color: '#334155', flexShrink: 0 }} />
        <span style={{ color: '#334155', fontSize: '13px' }}>—</span>
      </span>
    );
  }
  return (
    <span
      style={{
        fontSize: '13px',
        color: isGrowth ? '#E2E8F0' : '#E2E8F0',
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      {value}
    </span>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface PricingTableProps {
  showHero?: boolean;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PricingTable({ showHero = false }: PricingTableProps) {
  const [annual, setAnnual] = useState(false);
  const [activePlanIndex, setActivePlanIndex] = useState(1); // default Growth

  const getPrice = (monthlyPrice: number | null) => {
    if (monthlyPrice === null) return null;
    if (annual) return Math.round(monthlyPrice * 0.8);
    return monthlyPrice;
  };

  return (
    <div style={{ background: '#060B14' }}>
      {showHero && (
        <section
          style={{
            background: '#060B14',
            padding: '88px 40px 64px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse 65% 45% at 50% 0%, rgba(249,115,22,0.09), transparent 65%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              WebkitMaskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
              maskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#F97316',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                display: 'block',
                marginBottom: '18px',
              }}
            >
              Pricing
            </span>
            <h1
              style={{
                fontSize: 'clamp(36px, 5vw, 58px)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 1.05,
                color: 'white',
                marginBottom: '16px',
              }}
            >
              Simple, Transparent Pricing
            </h1>
            <p style={{ fontSize: '17px', color: '#94A3B8', lineHeight: 1.65 }}>
              Start with a 7-day free trial. No credit card required.
            </p>
          </div>
        </section>
      )}

      <section style={{ padding: showHero ? '0 20px 100px' : '0 20px 60px' }}>
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>

          {/* ── Monthly / Annual Toggle ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '14px',
              marginBottom: '52px',
              paddingTop: showHero ? '0' : '0',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: 600, color: !annual ? 'white' : '#64748B', transition: 'color 0.2s' }}>
              Monthly
            </span>
            <button
              onClick={() => setAnnual(!annual)}
              aria-label="Toggle annual billing"
              style={{
                position: 'relative',
                width: '52px',
                height: '28px',
                borderRadius: '100px',
                background: annual ? '#F97316' : 'rgba(255,255,255,0.1)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.25s ease',
                flexShrink: 0,
                padding: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '4px',
                  left: annual ? '28px' : '4px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'white',
                  transition: 'left 0.25s ease',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
              />
            </button>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: annual ? 'white' : '#64748B',
                transition: 'color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Annual
              <span
                style={{
                  background: annual ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.06)',
                  color: annual ? '#F97316' : '#64748B',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '3px 9px',
                  borderRadius: '100px',
                  letterSpacing: '0.02em',
                  transition: 'all 0.2s',
                }}
              >
                Save 20%
              </span>
            </span>
          </div>

          {/* ── Desktop: 5-column header grid ── */}
          <div className="pricing-desktop">
            {/* Header row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                gap: 0,
                borderRadius: '16px 16px 0 0',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.07)',
                borderBottom: 'none',
              }}
            >
              {/* Compare Plans label */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  padding: '28px 24px',
                  borderRight: '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Compare Plans
                </span>
              </div>

              {PLANS.map((plan) => {
                const price = getPrice(plan.monthlyPrice);
                const isGrowth = plan.id === 'growth';
                return (
                  <div
                    key={plan.id}
                    style={{
                      background: isGrowth ? 'rgba(249,115,22,0.04)' : 'rgba(255,255,255,0.02)',
                      borderLeft: isGrowth ? '1px solid rgba(249,115,22,0.2)' : '1px solid rgba(255,255,255,0.07)',
                      borderRight: isGrowth ? '1px solid rgba(249,115,22,0.2)' : 'none',
                      padding: '28px 20px',
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    {plan.badge && (
                      <div
                        style={{
                          display: 'inline-block',
                          background: plan.badgeBg ?? '#F97316',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: '100px',
                          marginBottom: '10px',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {plan.badge}
                      </div>
                    )}
                    {!plan.badge && <div style={{ height: '24px', marginBottom: '10px' }} />}
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>
                      {plan.name}
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      {price !== null ? (
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
                          <span style={{ fontSize: '32px', fontWeight: 900, color: 'white', letterSpacing: '-0.03em' }}>
                            ${price}
                          </span>
                          <span style={{ fontSize: '13px', color: '#64748B' }}>/mo</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '24px', fontWeight: 900, color: 'white' }}>Custom</span>
                      )}
                    </div>
                    <Link
                      href={plan.href}
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 700,
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        ...(plan.ctaStyle === 'orange'
                          ? {
                              background: 'linear-gradient(135deg, #F97316, #EA580C)',
                              color: 'white',
                              border: 'none',
                              boxShadow: '0 0 20px rgba(249,115,22,0.25)',
                            }
                          : plan.ctaStyle === 'dark'
                          ? {
                              background: 'rgba(255,255,255,0.04)',
                              color: '#94A3B8',
                              border: '1px solid rgba(255,255,255,0.08)',
                            }
                          : {
                              background: 'rgba(255,255,255,0.05)',
                              color: '#CBD5E1',
                              border: '1px solid rgba(255,255,255,0.09)',
                            }),
                      }}
                    >
                      {plan.cta}
                    </Link>
                    <p style={{ fontSize: '11px', color: '#475569', marginTop: '8px', lineHeight: 1.4 }}>
                      {plan.subline}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Feature comparison table */}
            <div
              style={{
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '0 0 16px 16px',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                </colgroup>
                <tbody>
                  {COMPARISON.map((section, sIdx) => (
                    <>
                      {/* Section header row */}
                      <tr
                        key={`section-${sIdx}`}
                        style={{
                          background: 'rgba(255,255,255,0.025)',
                          borderTop: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <td
                          colSpan={5}
                          style={{
                            padding: '10px 24px',
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#475569',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {section.section}
                        </td>
                      </tr>

                      {/* Feature rows */}
                      {section.rows.map((row, rIdx) => (
                        <tr
                          key={`${sIdx}-${rIdx}`}
                          style={{ borderTop: '1px solid rgba(255,255,255,0.04)', cursor: 'default' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.01)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                          }}
                        >
                          <td style={{ padding: '13px 24px', fontSize: '13px', color: '#94A3B8' }}>
                            {row.feature}
                          </td>
                          <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                            <CellValue value={row.starter} />
                          </td>
                          <td
                            style={{
                              padding: '13px 12px',
                              textAlign: 'center',
                              background: 'rgba(249,115,22,0.02)',
                              borderLeft: '1px solid rgba(249,115,22,0.1)',
                              borderRight: '1px solid rgba(249,115,22,0.1)',
                            }}
                          >
                            <CellValue value={row.growth} isGrowth />
                          </td>
                          <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                            <CellValue value={row.scale} />
                          </td>
                          <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                            <CellValue value={row.enterprise} />
                          </td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Mobile Carousel ── */}
          <div className="pricing-mobile">
            {(() => {
              const plan = PLANS[activePlanIndex];
              const price = getPrice(plan.monthlyPrice);
              const isGrowth = plan.id === 'growth';
              return (
                <div>
                  {/* Plan card */}
                  <div
                    style={{
                      background: isGrowth ? 'rgba(249,115,22,0.04)' : 'rgba(255,255,255,0.02)',
                      border: isGrowth ? '1.5px solid rgba(249,115,22,0.35)' : '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '16px',
                      padding: '32px 24px',
                      textAlign: 'center',
                      position: 'relative',
                      marginBottom: '16px',
                    }}
                  >
                    {plan.badge && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-13px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: plan.badgeBg ?? '#F97316',
                          color: 'white',
                          fontSize: '11px',
                          fontWeight: 700,
                          padding: '4px 14px',
                          borderRadius: '100px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {plan.badge}
                      </div>
                    )}
                    <h3 style={{ fontSize: '22px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>
                      {plan.name}
                    </h3>
                    <div style={{ marginBottom: '16px' }}>
                      {price !== null ? (
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
                          <span style={{ fontSize: '44px', fontWeight: 900, color: 'white', letterSpacing: '-0.03em' }}>
                            ${price}
                          </span>
                          <span style={{ fontSize: '14px', color: '#64748B' }}>/mo</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '34px', fontWeight: 900, color: 'white' }}>Custom</span>
                      )}
                    </div>
                    <Link
                      href={plan.href}
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        padding: '14px 20px',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: 700,
                        textDecoration: 'none',
                        marginBottom: '10px',
                        ...(plan.ctaStyle === 'orange'
                          ? {
                              background: 'linear-gradient(135deg, #F97316, #EA580C)',
                              color: 'white',
                              boxShadow: '0 0 30px rgba(249,115,22,0.2)',
                            }
                          : plan.ctaStyle === 'dark'
                          ? {
                              background: 'rgba(255,255,255,0.04)',
                              color: '#94A3B8',
                              border: '1px solid rgba(255,255,255,0.08)',
                            }
                          : {
                              background: 'rgba(255,255,255,0.05)',
                              color: '#CBD5E1',
                              border: '1px solid rgba(255,255,255,0.09)',
                            }),
                      }}
                    >
                      {plan.cta}
                    </Link>
                    <p style={{ fontSize: '12px', color: '#475569' }}>{plan.subline}</p>
                  </div>

                  {/* Carousel controls */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '32px' }}>
                    <button
                      onClick={() => setActivePlanIndex((i) => Math.max(0, i - 1))}
                      disabled={activePlanIndex === 0}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: activePlanIndex === 0 ? '#475569' : 'white',
                        cursor: activePlanIndex === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {PLANS.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => setActivePlanIndex(i)}
                          style={{
                            width: i === activePlanIndex ? '20px' : '6px',
                            height: '6px',
                            borderRadius: '100px',
                            background: i === activePlanIndex ? '#F97316' : 'rgba(255,255,255,0.2)',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            padding: 0,
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: '12px', color: '#64748B', minWidth: '60px', textAlign: 'center' }}>
                      {PLANS[activePlanIndex].name}
                    </span>
                    <button
                      onClick={() => setActivePlanIndex((i) => Math.min(PLANS.length - 1, i + 1))}
                      disabled={activePlanIndex === PLANS.length - 1}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: activePlanIndex === PLANS.length - 1 ? '#475569' : 'white',
                        cursor: activePlanIndex === PLANS.length - 1 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>

                  {/* Feature list for active plan on mobile */}
                  <div
                    style={{
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}
                  >
                    {COMPARISON.map((section, sIdx) => (
                      <div key={sIdx}>
                        <div
                          style={{
                            background: 'rgba(255,255,255,0.025)',
                            borderTop: sIdx > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                            padding: '8px 16px',
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#475569',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {section.section}
                        </div>
                        {section.rows.map((row, rIdx) => {
                          const planKey = plan.id as keyof typeof row;
                          const val = row[planKey] as CellVal;
                          return (
                            <div
                              key={rIdx}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '11px 16px',
                                borderTop: '1px solid rgba(255,255,255,0.04)',
                              }}
                            >
                              <span style={{ fontSize: '13px', color: '#94A3B8' }}>{row.feature}</span>
                              <CellValue value={val} isGrowth={isGrowth} />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      <style>{`
        .pricing-desktop { display: block; }
        .pricing-mobile { display: none; }
        @media (max-width: 767px) {
          .pricing-desktop { display: none !important; }
          .pricing-mobile { display: block !important; }
        }
      `}</style>
    </div>
  );
}
